<template>
  <div class="content-ownership-analytics-route" v-if="contentOwnerships.length">
    <Panel>
      <div class="dateselectorwrapper">
        <div class="dateandcontentselector">
          <div class="contentinfo">
            <img :src="selectedContentOwnership.imageUrl"/>
            <UISelect :title="$t('Content')" v-model="selectedContentOwnershipId">
              <option
                v-for="contentOwnership of contentOwnerships"
                :key="contentOwnership.id"
                :value="contentOwnership.id"
              >{{ contentOwnership.title }}</option>
            </UISelect>
          </div>
          <div class="dateranges">
            <div
              v-for="(dateRange, index) in dateRanges"
              :key="index"
              class="daterange"
              :class="{ '-threegrid': dateRanges.length > 1 }"
            >
              <div
                class="color"
                v-if="dateRanges.length > 1"
                :style="{ 'background-color': colorArray[index] }"
              ></div>
              <DateRange :dateRange="dateRange" @update:dateRange="updateDateRange(index, $event)" />
            </div>
          </div>
          <div class="buttons">
            <UIButton :disabled="refreshing" @click="refreshAnalytics">{{ $t('Refresh Data') }}</UIButton>
          </div>
        </div>
      </div>
    </Panel>

    <div class="container">
      <ChartJSStatPanel
        class="monthlyminutes"
        minHeightLoading="10rem"
        :title="$t('Minutes Over Previous Year')"
        :chartOptions="monthlyMinutes"
        :loading="monthlyMinutes === null"
        :tooltip="tooltips.monthlyMinutes"
      />

      <div class="four-grid">
        <BigNumberStatPanel
          :title="$t('Ranking on SpringboardVR')"
          :numbers="[`#${rating.rank}`]"
          :loading="!rating.rank || refreshing"
          :tooltip="tooltips.rating.rank"
        />
        <BigNumberStatPanel
          :title="$t('Average Customer Score')"
          :numbers="[rating.percentage]"
          :loading="!rating.percentage || refreshing"
          :tooltip="tooltips.rating.percentage"
        />
        <BigNumberStatPanel
          :title="$t('# Of Customer Scores')"
          :numbers="[rating.count]"
          :loading="!rating.count || refreshing"
        />
        <BigNumberStatPanel
          :title="$t('Average Session Length in Minutes')"
          :numbers="[rating.average]"
          :loading="!rating.average || refreshing"
        />
      </div>

      <StatPanel
        :title="$t('Usage')"
        :loading="total.enabled === null || refreshing"
        :tooltip="tooltips.usage"
      >
        <table class="f-body">
          <tr class="f-tiny">
            <th></th>
            <th v-if="total.enabled">{{ $t('Total') }}</th>
            <th v-if="minute.enabled">{{ $t('Per-Minute License') }}</th>
            <th v-if="month.enabled">{{ $t('Monthly License') }}</th>
            <th v-if="year.enabled">{{ $t('Yearly License') }}</th>
          </tr>
          <tr>
            <td>{{ $t('Minutes') }}</td>
            <td v-if="total.enabled">{{ total.minutes }}</td>
            <td v-if="minute.enabled">{{ minute.minutes }}</td>
            <td v-if="month.enabled">{{ month.minutes }}</td>
            <td v-if="year.enabled">{{ year.minutes }}</td>
          </tr>
          <tr>
            <td>{{ $t('Earnings*')}}</td>
            <td v-if="total.enabled">${{ total.earnings }} USD</td>
            <td v-if="minute.enabled">${{ minute.earnings }} USD</td>
            <td v-if="month.enabled">${{ month.earnings }} USD</td>
            <td v-if="year.enabled">${{ year.earnings }} USD</td>
          </tr>
          <tr>
            <td>{{ $t('Licenses') }}</td>
            <td v-if="total.enabled">{{ total.licenses }}</td>
            <td v-if="minute.enabled">{{ minute.licenses }}</td>
            <td v-if="month.enabled">{{ month.licenses }}</td>
            <td v-if="year.enabled">{{ year.licenses }}</td>
          </tr>
          <tr>
            <td>{{ $t('Locations') }}</td>
            <td v-if="total.enabled">{{ total.locations }}</td>
            <td v-if="minute.enabled">{{ minute.locations }}</td>
            <td v-if="month.enabled">{{ month.locations }}</td>
            <td v-if="year.enabled">{{ year.locations }}</td>
          </tr>
          <tr>
            <td>{{ $t('Stations') }}</td>
            <td v-if="total.enabled">{{ total.stations }}</td>
            <td v-if="minute.enabled">{{ minute.stations }}</td>
            <td v-if="month.enabled">{{ month.stations }}</td>
            <td v-if="year.enabled">{{ year.stations }}</td>
          </tr>
        </table>
        <p class="analyticsAddition">
          {{ $t('*Monthly payouts are calculated based on collected earnings, not total earnings shown in analytics.') }}
          </p>
      </StatPanel>

      <WorldMap
        :title="$t('World')"
        :loading="geoCountriesWithMinutes === null"
        :data="geoCountriesWithMinutes"
        :tooltip="tooltips.geoCountriesWithMinutes"
      />

      <NAMap
        :title="$t('Canada and USA')"
        :loading="geoNorthAmericaRegionsWithMinutes === null"
        :data="geoNorthAmericaRegionsWithMinutes"
        :tooltip="tooltips.geoNorthAmericaRegionsWithMinutes"
      />

      <ContentBlock>
        <template #instructions>
          <h2 class="f-header mb3">{{ $t('Export Data') }}</h2>
          <p
            class="f-body"
          >{{ $t("Export Your location's data in CSV format. The data will include the dates selected at the top of the page.") }}</p>
        </template>
        <div class="exportbuttons">
          <UIButton
            :disabled="exportLoading"
            class="export"
            secondary
            @click="doRequestContentOwnershipExportWithLoading(dateRanges, 'unlicensed')"
          >{{ $t('Export Unlicensed Usage Data') }}</UIButton>
          <UIButton
            :disabled="exportLoading"
            class="export"
            secondary
            @click="doRequestContentOwnershipExportWithLoading(dateRanges, 'licensed')"
          >{{ $t('Export Licensed Usage Data') }}</UIButton>
          <UIButton
            :disabled="exportLoading"
            class="export"
            secondary
            @click="doRequestContentOwnershipExportWithLoading(dateRanges, 'countries')"
          >{{ $t('Export Country Usage Data') }}</UIButton>
        </div>
      </ContentBlock>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ref, computed, watch } from '@vue/composition-api';
import { useContentOwnershipAnalytics } from './compositions/useContentOwnershipAnalytics';
import WorldMap from '@/core/shared/components/analytics/stat-panels/world-map/WorldMap';
import NAMap from '@/core/shared/components/analytics/stat-panels/na-map/NAMap';
import ChartJSStatPanel from '@/core/shared/components/analytics/stat-panels/ChartJSStatPanel';
import BigNumberStatPanel from '@/core/shared/components/analytics/stat-panels/BigNumberStatPanel';
import StatPanel from '@/core/shared/components/analytics/stat-panels/StatPanel';
import UIButton from '@/core/shared/components/ui/UIButton';
import UISelect from '@/core/shared/components/ui/UISelect';
import Panel from '@/core/shared/components/Panel';
import ContentBlock from '@/core/shared/components/ContentBlock';
import DateRange from '@/core/shared/components/analytics/DateRange';
import moment from 'moment-timezone';
import { useStore, useI18n } from '@/core/shared/compositions';

export function useContentOwnerships (getData) {
  const store = useStore();

  const selectedContentOwnershipId = ref('');
  const contentOwnerships = computed(() => store.getters['entities/ContentOwnership/all']());
  const selectedContentOwnership = computed(() => store.getters['entities/ContentOwnership'](selectedContentOwnershipId.value));

  watch(contentOwnerships, () => {
    if (contentOwnerships.value.length) {
      selectedContentOwnershipId.value = contentOwnerships.value[0].id;
    }
  });

  return {
    contentOwnerships,
    selectedContentOwnershipId,
    selectedContentOwnership,
  };
}

export function useDateRanges () {
  const refreshing = ref(false);
  const dateRanges = ref([]);

  function addDateRange () {
    dateRanges.value = [
      ...dateRanges.value,
      {
        startTime: moment().date(1).format(),
        endTime: moment().format(),
      },
    ];
  }

  function updateDateRange (index, dateRange) {
    Vue.set(dateRanges.value, index, dateRange);
  }

  function removeDateRange (index) {
    dateRanges.value = dateRanges.value.slice(0, index).concat(dateRanges.value.slice(index + 1));
  }

  addDateRange();

  return {
    dateRanges,
    addDateRange,
    updateDateRange,
    removeDateRange,

    refreshing,
  };
}

export default {
  components: {
    DateRange,
    UIButton,
    UISelect,
    Panel,
    ChartJSStatPanel,
    BigNumberStatPanel,
    StatPanel,
    WorldMap,
    NAMap,
    ContentBlock,
  },
  setup (props) {
    const store = useStore();
    const i18n = useI18n();

    store.commit('setTitle', i18n.t('Content Ownership Analytics'));

    const {
      selectedContentOwnershipId,
      contentOwnerships,
      selectedContentOwnership,
    } = useContentOwnerships();

    const {
      doGetContentOwnershipAnalytics,
      doRequestContentOwnershipExport,

      monthlyMinutes,

      geoCountriesWithMinutes,
      geoNorthAmericaRegionsWithMinutes,

      total,
      minute,
      month,
      year,

      tooltips,
      colorArray,

      rating,
    } = useContentOwnershipAnalytics(selectedContentOwnershipId);

    const {
      dateRanges,
      addDateRange,
      updateDateRange,
      removeDateRange,

      refreshing,
    } = useDateRanges();

    async function refreshAnalytics () {
      refreshing.value = true;
      await doGetContentOwnershipAnalytics(dateRanges.value);
      refreshing.value = false;
    };

    const exportLoading = ref(false);
    async function doRequestContentOwnershipExportWithLoading (dateRanges, dataType) {
      exportLoading.value = true;
      await doRequestContentOwnershipExport(dateRanges, dataType);
      exportLoading.value = false;
    }

    watch(selectedContentOwnershipId, (newVal, oldVal) => {
      if (newVal) refreshAnalytics();
    });

    return {
      contentOwnerships,
      selectedContentOwnershipId,
      selectedContentOwnership,

      monthlyMinutes,

      geoCountriesWithMinutes,
      geoNorthAmericaRegionsWithMinutes,

      total,
      minute,
      month,
      year,

      rating,

      dateRanges,
      addDateRange,
      updateDateRange,
      removeDateRange,
      refreshing,

      refreshAnalytics,

      tooltips,
      colorArray,

      exportLoading,
      doRequestContentOwnershipExportWithLoading,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.content-ownership-analytics-route {
  & .contentinfo {
    display: grid;
    grid-gap: var(--spacingMd);
    align-items: center;
    grid-template-columns: 10rem auto;

    & img {
      width: 10rem;
    }
  }

  & .dateselectorwrapper {
    display: flex;
    justify-content: center;

    & .dateandcontentselector {
      flex: 1 0 100%;
      max-width: 120rem;
      display: grid;
      grid-gap: var(--spacingSm);
    }

    & .dateranges {
      margin-bottom: var(--spacingSm);
      display: grid;
      grid-gap: var(--spacingSm);

      & .daterange {
        position: relative;

        & .close {
          position: absolute;
          width: 2rem;
          height: 2rem;
          top: calc(var(--spacingSm) * -1);
          right: calc(var(--spacingSm) * -1);
        }

        & .color {
          width: 3rem;
          height: 3rem;
        }

        &.-threegrid {
          display: grid;
          grid-gap: var(--spacingSm);
          align-items: center;
          grid-template-columns: auto 1fr;
        }
      }
    }

    & .buttons {
      display: grid;
      grid-gap: var(--spacingMd);

      @media (--tablet) {
        grid-template-columns: auto;
        justify-content: end;
      }
    }
  }

  & .four-grid {
    display: grid;
    grid-gap: var(--spacingMd);

    @media (--tablet) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  & table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;
    border-radius: var(--radius);
    border: 0.15rem solid var(--colorGeyser);
    overflow: hidden;

    & th {
      padding: var(--spacingSm);
      text-align: left;
    }

    & tr {
      border-bottom: solid 0.1rem var(--colorGeyser);

      &::last-child {
        border-bottom: none;
      }
    }

    & td {
      padding: var(--spacingSm);
      text-align: left;
      vertical-align: middle;
    }
  }

  & .container {
    display: grid;
    grid-gap: var(--spacingMd);
    margin-top: var(--spacingMd);
  }

  & .exportbuttons {
    display: grid;
    grid-gap: var(--spacingMd);
    align-items: end;
  }

  .analyticsAddition {
    font-size: 1.2rem;
  }
}
</style>
